import { FC, useState, useContext, useEffect, useRef } from 'react';
import { FormContext } from '../FormContext';
import { handleValidation, ValidatorRule } from '../validation';
import classNames from 'classnames';

type NAUTextareaProps = {
  label: string;
  value?: string;
  description?: React.ReactNode;
  defaultValue?: string;
  inputMode?:
    | 'text'
    | 'numeric'
    | 'decimal'
    | 'email'
    | 'search'
    | 'tel'
    | 'url';
  placeholder?: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  validators?: ValidatorRule[];
  rows?: number;
  name?: string;
};

const NAUTextarea: FC<NAUTextareaProps> = ({
  label,
  description,
  value,
  defaultValue,
  inputMode,
  placeholder,
  onChange,
  onKeyDown,
  onPaste,
  validators,
  rows = 3,
  name,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { registerInput, setInputError, registerValidation } =
    useContext(FormContext);

  useEffect(() => {
    registerInput(label, textareaRef);
    if (validators) {
      registerValidation(label, () =>
        handleValidation(
          textareaRef.current?.value || '',
          validators,
          setErrorMessage,
          setInputError,
          label,
        ),
      );
    }
  }, []);

  const [errorMessage, setErrorMessage] = useState('');

  return (
    <div>
      <label
        className={classNames(
          'form-label',
          validators?.includes('required') && 'required',
        )}
      >
        {label}
        {description && (
          <span className="form-label-description">{description}</span>
        )}
      </label>
      <textarea
        name={name}
        rows={rows}
        className={`form-control ${errorMessage ? 'is-invalid' : ''}`}
        required={validators?.includes('required')}
        value={value}
        defaultValue={defaultValue}
        inputMode={inputMode}
        placeholder={placeholder}
        ref={textareaRef}
        onChange={(e) => {
          onChange(e);
          handleValidation(
            e.target.value,
            validators,
            setErrorMessage,
            setInputError,
            label,
          );
        }}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
      />
      {errorMessage !== '' && (
        <div className="invalid-feedback">{errorMessage}</div>
      )}
    </div>
  );
};

export default NAUTextarea;
